"use client"

import { useState } from "react"
import { DISCORD_INVITE_URL } from "@/lib/discord-invite"
import { SHOW_DISCORD } from "@/lib/discord-visibility"
import { Rocket, DiscordLogo, X, } from "@/lib/icons"
import { Button } from "@/components/ui/button"
import { useRouter } from "next/navigation"
import { useModalContent } from "@/lib/modal-content"
import { useAuthStore } from "@/lib/auth-store"
import { markSeen } from "@/lib/client/seen-state"
import { useTranslations } from "next-intl"

export const EARLY_ACCESS_SEEN_KEY = "modal:early_access"
export const LOCAL_EARLY_ACCESS_SEEN_KEY = "goonru_early_access_seen"

const DISCORD_URL = DISCORD_INVITE_URL

/** Presentational content for the Early Access welcome, extracted from what
 * used to be the standalone EarlyAccessModal so day-one-promo-stack.tsx can
 * own eligibility/sequencing (alongside Glymtober) while this only ever
 * renders once the stack has already decided to show it. The whole modal's
 * copy (badge, headline, subhead, bullets) is baked into the hero graphic
 * (default /mascot/glym-early-access.webp, replaceable in admin) — nothing
 * else to render except the buttons below it. */
export function EarlyAccessCard({ onDone, onReady }: { onDone: () => void; onReady?: () => void }) {
  const t = useTranslations("earlyAccessModal")
  const content = useModalContent("early_access", true)
  const [heroLoaded, setHeroLoaded] = useState(true)
  const { user } = useAuthStore()
  const router = useRouter()

  const dismiss = () => {
    if (user) {
      void markSeen(EARLY_ACCESS_SEEN_KEY)
    } else {
      try { localStorage.setItem(LOCAL_EARLY_ACCESS_SEEN_KEY, "1") } catch {}
    }
    onDone()
  }

  if (!heroLoaded || !content.ready) return null

  return (
    <div className="relative w-full bg-background rounded-lg shadow-2xl overflow-hidden">
      <button
        onClick={dismiss}
        className="absolute top-3 right-3 z-10 h-8 w-8 rounded-full bg-black/45 backdrop-blur-md border border-white/10 flex items-center justify-center text-white/90 opacity-50 hover:opacity-100 hover:bg-black/65 hover:text-white active:opacity-100 focus-visible:opacity-100 transition-opacity"
      >
        <X className="h-4 w-4" />
      </button>

      <div className="relative">
        {/* Plain <img>: an uploaded override lives on the storage host, which
            next/image would need whitelisting for. */}
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={content.art("hero")}
          alt={t("welcomeToGoonruEarlyAccess")}
          className="w-full h-auto block"
          ref={(el) => { if (el && el.complete && el.naturalWidth > 0) onReady?.() }}
          onLoad={() => onReady?.()}
          onError={() => { setHeroLoaded(false); onReady?.() }}
        />

        {/* Buttons float on top of the art itself, over a bottom scrim for contrast */}
        <div className="absolute inset-x-0 bottom-10 px-4 flex justify-center">
          <div className="inline-flex flex-col items-stretch gap-2.5">
            <div className="flex gap-2.5">
              {SHOW_DISCORD && (
                <Button
                  className="rounded-full px-4 h-9 bg-[#5865F2]/90 hover:bg-[#5865F2] backdrop-blur-md text-white border border-white/15 shadow-[0_0_16px_rgba(88,101,242,0.5)]"
                  onClick={() => { window.open(DISCORD_URL, "_blank") }}
                >
                  <DiscordLogo className="h-4 w-4 mr-1.5" />
                  {content.text("join_discord", t("joinDiscord"))}
                </Button>
              )}
              <Button
                className="rounded-full px-4 h-9 bg-black/40 hover:bg-black/55 backdrop-blur-md text-amber-300 border border-amber-400/40 shadow-[0_0_16px_rgba(240,180,41,0.25)]"
                onClick={() => { dismiss(); router.push("/subscribe") }}
              >
                <Rocket className="h-4 w-4 mr-1.5" />
                {content.text("support_us", t("supportUs"))}
              </Button>
            </div>

            <button
              onClick={dismiss}
              className="w-full rounded-full px-5 py-2 text-xs font-medium text-white/70 bg-white/5 hover:bg-white/10 hover:text-white/95 border border-white/10 transition-colors"
            >
              {content.text("got_it", t("gotItLetSGo"))}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
